import React, { useRef } from "react";
import { motion, useScroll, useTransform, useMotionTemplate, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowDown } from "lucide-react";

// Hero exit: a quick, clean cinematic dissolve. As soon as the user scrolls,
// the title softly blurs and fades away (no cropping, no lingering) and the
// next section arrives immediately — the hero is exactly one viewport tall.
const WORDS = [
  { text: "MUSIC", accent: false },
  { text: "GROWTH", accent: true },
  { text: "SYSTEMS", accent: false },
];

const HeroWord = ({ word, index, scrollY, reduce, entranceDelay }) => {
  // Pixel-based mapping: quick, deterministic dissolve within the first
  // ~350px of scroll. Slight stagger per word keeps it cinematic.
  const start = 20 + index * 25;
  const end = 300 + index * 25;

  const opacity = useTransform(scrollY, [start, end], [1, 0]);
  const y = useTransform(scrollY, [start, end], [0, reduce ? 0 : -24]);
  const blurAmount = useTransform(scrollY, [start, end], [0, reduce ? 0 : 8]);
  const filter = useMotionTemplate`blur(${blurAmount}px)`;

  return (
    <motion.span
      className="block transform-gpu will-change-[transform,filter,opacity]"
      style={{ opacity, y, filter }}
    >
      <motion.span
        className="inline-block"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: entranceDelay, ease: [0.2, 0.8, 0.2, 1] }}
      >
        {word.accent ? <span className="text-[color:var(--hs-lime)]">{word.text}</span> : word.text}
      </motion.span>
    </motion.span>
  );
};

export const Hero = ({ onPrimaryCTA }) => {
  const sectionRef = useRef(null);
  const reduce = useReducedMotion();

  const { scrollY } = useScroll();

  // Kicker, subline and CTAs dissolve alongside the title (pixel-based)
  const uiOpacity = useTransform(scrollY, [0, 180], [1, 0]);
  const uiY = useTransform(scrollY, [0, 220], [0, reduce ? 0 : -30]);
  const hintOpacity = useTransform(scrollY, [0, 100], [1, 0]);
  const topBarOpacity = useTransform(scrollY, [0, 260], [1, 0]);

  const scrollToWork = () => {
    const el = document.getElementById("artists");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={sectionRef} className="relative h-[100svh]" data-testid="hero-section">
      <div className="relative h-full overflow-hidden">
        {/* Top bar */}
        <motion.div className="absolute top-0 left-0 right-0 z-20" style={{ opacity: topBarOpacity }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
            <img
              src="/logo.png"
              alt="Holdstill Records"
              className="h-9 sm:h-11 w-auto drop-shadow-lg"
              data-testid="hero-logo"
            />
            <Button
              onClick={onPrimaryCTA}
              data-testid="nav-cta-button"
              className="btn-lift bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl px-4 sm:px-5 h-10 text-sm"
            >
              Book Free Consult
            </Button>
          </div>
        </motion.div>

        {/* Title stage */}
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-4 text-center">
          <motion.p
            className="font-mono-ui text-[11px] sm:text-xs tracking-[0.35em] uppercase text-[color:var(--hs-lime)] mb-4 sm:mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            style={{ opacity: uiOpacity, y: uiY }}
          >
            Holdstill Records · Est. 2024
          </motion.p>

          <h1
            className="font-display font-display-rough uppercase text-white leading-[0.92] text-[17vw] sm:text-[11vw] lg:text-[9rem] select-none"
            data-testid="hero-heading"
          >
            {WORDS.map((word, wi) => (
              <HeroWord
                key={word.text}
                word={word}
                index={wi}
                scrollY={scrollY}
                reduce={reduce}
                entranceDelay={0.15 + wi * 0.14}
              />
            ))}
          </h1>

          <motion.div style={{ opacity: uiOpacity, y: uiY }}>
            <motion.p
              className="mt-5 sm:mt-7 max-w-xl text-sm sm:text-base text-white/80"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
            >
              End-to-end growth for artists and music businesses.
              <br className="hidden sm:block" />
              Built by musicians. 20+ years in the Indian scene.
            </motion.p>
            <motion.div
              className="mt-7 sm:mt-9 flex flex-col sm:flex-row items-center justify-center gap-3"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
            >
              <Button
                onClick={onPrimaryCTA}
                data-testid="hero-primary-cta-button"
                className="btn-lift glow-lime bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl h-12 px-7 text-base"
              >
                Book Free 20-Min Consult
              </Button>
              <Button
                onClick={scrollToWork}
                variant="outline"
                data-testid="hero-secondary-cta-button"
                className="btn-lift bg-black/30 backdrop-blur-sm border-white/25 text-white hover:border-[color:rgba(202,255,26,0.55)] hover:bg-[color:rgba(202,255,26,0.08)] hover:text-white rounded-xl h-12 px-7 text-base"
              >
                See How It Works
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll hint */}
        <motion.div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1.5 text-white/70"
          style={{ opacity: hintOpacity }}
        >
          <span className="font-mono-ui text-[10px] tracking-[0.3em] uppercase">Scroll</span>
          <ArrowDown className="h-4 w-4 animate-bounce text-[color:var(--hs-lime)]" />
        </motion.div>
      </div>
    </section>
  );
};
